import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, MessageCircle, HelpCircle, ArrowLeft } from 'lucide-react';
import './LegalPages.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const RECAPTCHA_SITE_KEY = '6Lch-PkrAAAAAIVzw1FvxWsmn33OIdorSdD4DtUs';

// Tawk.to Live Chat IDs
const TAWK_PROPERTY_ID = '68ff7bece7c8851951477b6a';
const TAWK_WIDGET_ID = '1j8ivo59h';

const Support = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const [chatOnline, setChatOnline] = useState(false);

  // Load Tawk.to Chat
  useEffect(() => {
    // Only load if valid IDs
    if (TAWK_PROPERTY_ID === 'YOUR_PROPERTY_ID') {
      console.log('⚠️ Tawk.to IDs not configured');
      return;
    }

    window.Tawk_API = window.Tawk_API || {};
    window.Tawk_LoadStart = new Date();

    // Status Callback
    window.Tawk_API.onStatusChange = (status) => {
      console.log('Tawk.to status:', status);
      setChatOnline(status === 'online');
    };

    // Load Script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://embed.tawk.to/${TAWK_PROPERTY_ID}/${TAWK_WIDGET_ID}`;
    script.charset = 'UTF-8';
    script.setAttribute('crossorigin', '*');
    
    script.onload = () => {
      console.log('✅ Tawk.to loaded');
    };

    const firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode.insertBefore(script, firstScript);

    return () => {
      try {
        if (window.Tawk_API && window.Tawk_API.hideWidget) {
          window.Tawk_API.hideWidget();
        }
      } catch (err) {
        // Ignore
      }
    };
  }, []);

  // Load reCAPTCHA Script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      console.log('✅ reCAPTCHA loaded');
      setRecaptchaLoaded(true);
    };
    document.head.appendChild(script);

    return () => {
      try {
        const scriptTag = document.querySelector(`script[src*="recaptcha"]`);
        if (scriptTag && scriptTag.parentNode) {
          scriptTag.parentNode.removeChild(scriptTag);
        }
      } catch (err) {
        // Ignore
      }
    };
  }, []);

  // Open Live Chat
  const openChat = () => {
    if (window.Tawk_API && window.Tawk_API.maximize) {
      window.Tawk_API.maximize();
      console.log('📞 Chat opened');
    } else {
      console.log('⚠️ Chat not available');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let recaptchaToken = null;
      
      if (window.grecaptcha && recaptchaLoaded) {
        try {
          recaptchaToken = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { 
            action: 'support_form' 
          });
          console.log('✅ reCAPTCHA token generated');
        } catch (recaptchaError) {
          console.warn('⚠️ reCAPTCHA generation failed:', recaptchaError);
        }
      }

      const response = await fetch(`${API_URL}/support/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          recaptchaToken
        })
      });

      const data = await response.json();

      if (response.ok) {
        console.log('✅ Support-Anfrage erfolgreich gesendet');
        setSubmitted(true);
        
        setTimeout(() => {
          setSubmitted(false);
          setFormData({ name: '', email: '', subject: '', message: '' });
        }, 5000);
      } else {
        setError(data.error || t('support.form.error.default'));
        console.error('❌ Support-Anfrage fehlgeschlagen:', data);
      }
    } catch (error) {
      console.error('❌ Netzwerkfehler:', error);
      setError(t('support.form.error.network'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="legal-page">
      <header className="legal-header">
        <div className="legal-header-content">
          <button onClick={() => navigate('/')} className="back-button">
            <ArrowLeft size={20} />
            <span>{t('support.header.backButton')}</span>
          </button>
          <div className="legal-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img src="/assets/logo/alpenscreen-horizontal-transparent.svg" alt="AlpenScreen" className="logo-img" />
          </div>
        </div>
      </header>

      <div className="legal-container">
        <div className="legal-content">
          <h1 className="legal-title">{t('support.title')}</h1>
          <p className="legal-intro">
            {t('support.intro')}
          </p>

          {/* Quick Contact Options */}
          <div className="support-options">
            <div className="support-card">
              <div className="support-icon">
                <Mail size={32} color="#2B7FD7" />
              </div>
              <h3>{t('support.options.email.title')}</h3>
              <p>
                <a 
                  href="mailto:support@alpenscreen.ch" 
                  style={{ color: '#2B7FD7', textDecoration: 'none' }}
                >
                  support@alpenscreen.ch
                </a>
              </p>
              <p className="support-note">{t('support.options.email.responseTime')}</p>
            </div>

            <div 
              className="support-card" 
              onClick={openChat}
              style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <div className="support-icon">
                <MessageCircle size={32} color="#2B7FD7" />
              </div>
              <h3>{t('support.options.chat.title')}</h3>
              <p>{t('support.options.chat.hours')}</p>
              <div style={{ 
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 12px', 
                borderRadius: '12px',
                background: chatOnline ? '#10b981' : '#6b7280',
                color: 'white',
                fontSize: '12px',
                fontWeight: 'bold',
                marginTop: '8px'
              }}>
                <span style={{ 
                  width: '8px', 
                  height: '8px', 
                  borderRadius: '50%', 
                  background: 'white',
                  display: 'inline-block'
                }}></span>
                {chatOnline ? t('support.options.chat.status.online') : t('support.options.chat.status.offline')}
              </div>
              <p className="support-note" style={{ marginTop: '8px' }}>
                {t('support.options.chat.clickToOpen')}
              </p>
            </div>

            <div className="support-card">
              <div className="support-icon">
                <HelpCircle size={32} color="#2B7FD7" />
              </div>
              <h3>{t('support.options.helpCenter.title')}</h3>
              <p>{t('support.options.helpCenter.description')}</p>
              <p className="support-note">{t('support.options.helpCenter.availability')}</p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="contact-form-section">
            <h2>{t('support.form.title')}</h2>
            <p>{t('support.form.description')}</p>

            {submitted ? (
              <div style={{
                background: '#10b981',
                color: 'white',
                padding: '30px',
                borderRadius: '12px',
                textAlign: 'center',
                margin: '20px 0'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>✓</div>
                <div style={{ fontSize: '20px', marginBottom: '8px' }}>
                  {t('support.form.success.title')}
                </div>
                <div style={{ fontSize: '14px', opacity: 0.9 }}>
                  {t('support.form.success.message')}
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="contact-form">
                {error && (
                  <div style={{
                    background: '#ef4444',
                    color: 'white',
                    padding: '16px',
                    borderRadius: '8px',
                    marginBottom: '24px'
                  }}>
                    {error}
                  </div>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('support.form.fields.name.label')}</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder={t('support.form.fields.name.placeholder')}
                      disabled={loading}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('support.form.fields.email.label')}</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder={t('support.form.fields.email.placeholder')}
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>{t('support.form.fields.subject.label')}</label>
                  <input
                    type="text"
                    required
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder={t('support.form.fields.subject.placeholder')}
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label>{t('support.form.fields.message.label')}</label>
                  <textarea
                    required
                    rows="6"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder={t('support.form.fields.message.placeholder')}
                    disabled={loading}
                  />
                </div>

                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={loading}
                >
                  {loading ? t('support.form.submitting') : t('support.form.submit')}
                </button>

                <p style={{ 
                  fontSize: '12px', 
                  color: '#666', 
                  marginTop: '16px',
                  textAlign: 'center'
                }}>
                  {t('support.form.recaptcha.text')}{' '}
                  <a 
                    href="https://policies.google.com/privacy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ color: '#2B7FD7' }}
                  >
                    {t('support.form.recaptcha.privacy')}
                  </a>
                  {' '}{t('support.form.recaptcha.and')}{' '}
                  <a 
                    href="https://policies.google.com/terms" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ color: '#2B7FD7' }}
                  >
                    {t('support.form.recaptcha.terms')}
                  </a>
                  {' '}{t('support.form.recaptcha.google')}
                </p>
              </form>
            )}
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h2>{t('support.faq.title')}</h2>
            
            <div className="faq-item">
              <h3>{t('support.faq.q1.question')}</h3>
              <p>{t('support.faq.q1.answer')}</p>
            </div>

            <div className="faq-item">
              <h3>{t('support.faq.q2.question')}</h3>
              <p>{t('support.faq.q2.answer')}</p>
            </div>

            <div className="faq-item">
              <h3>{t('support.faq.q3.question')}</h3>
              <p dangerouslySetInnerHTML={{ __html: t('support.faq.q3.answer') }}></p>
            </div>

            <div className="faq-item">
              <h3>{t('support.faq.q4.question')}</h3>
              <p>{t('support.faq.q4.answer')}</p>
            </div>
          </div>

          {/* Office Hours */}
          <div className="office-hours">
            <h2>{t('support.hours.title')}</h2>
            <div className="hours-grid">
              <div className="hours-item">
                <strong>{t('support.hours.weekdays')}</strong>
                <p>{t('support.hours.weekdaysTime')}</p>
              </div>
              <div className="hours-item">
                <strong>{t('support.hours.weekend')}</strong>
                <p>{t('support.hours.weekendTime')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer className="legal-footer">
        <div className="legal-footer-content">
          <p>{t('support.footer.copyright')}</p>
          <div className="legal-footer-links">
            <button onClick={() => navigate('/privacy')} className="footer-link">{t('support.footer.privacy')}</button>
            <span>•</span>
            <button onClick={() => navigate('/terms')} className="footer-link">{t('support.footer.terms')}</button>
            <span>•</span>
            <button onClick={() => navigate('/imprint')} className="footer-link">{t('support.footer.imprint')}</button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Support;